import { Link } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import { ArrowRight, HeartPulse, Bone, Ribbon, Flower2, FileText, CalendarClock, Plane, User } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal, SectionHeading } from "@/components/motion";
import { CtaBand } from "@/components/CtaBand";
import { LeafMark } from "@/components/LeafMark";
import { usePageMeta } from "@/lib/seo";
import { IMG } from "@/lib/site";

const ICONS = { heart: HeartPulse, bone: Bone, ribbon: Ribbon, flower: Flower2, file: FileText, calendar: CalendarClock, plane: Plane, user: User };

export const MedicalCare = () => {
    const {  t, tHtml, site: siteData } = useLang();
    const COORDINATED_ITEMS = [
        {
            title: t("medical_care.coord1.title", "Medical opinions & second opinions"),
            desc: t("medical_care.coord1.desc", "Your reports reviewed by appropriate specialists — so you can move forward with confidence."),
        },
        {
            title: t("medical_care.coord2.title", "Treatment planning"),
            desc: t("medical_care.coord2.desc", "Clear explanation of options, requirements and realistic timelines, step by step."),
        },
        {
            title: t("medical_care.coord3.title", "Hospital coordination"),
            desc: t("medical_care.coord3.desc", "Appointments, diagnostics and admissions coordinated with the provider caring for you."),
        },
        {
            title: t("medical_care.coord4.title", "International patient support"),
            desc: t("medical_care.coord4.desc", "Travel planning, accommodation, language assistance and a single point of contact throughout."),
        },
    ];
    const { SPECIALTIES, HOW_IT_WORKS } = siteData;
    usePageMeta(
        t("meta.medical_care.title", "Medical Care in India — Specialist Healthcare & Coordination"),
        t("meta.medical_care.desc", "From medical opinions and second opinions to treatment planning and hospital coordination, UniversHeal helps international patients navigate healthcare in India.")
    );
    return (
        <>
            <PageHero
                eyebrow={t("medical_care.eyebrow", "Medical Care")}
                title={
                    <>
                        Specialist healthcare,{" "}
                        <span className="italic text-pine">coordinated around you.</span>
                    </>
                }
                intro={t("medical_care.intro", "From medical opinions and second opinions to treatment planning and hospital coordination, UniversHeal helps international patients navigate healthcare in India.")}
                testid="medical-care-hero"
            />

            <section className="section-pad pt-10">
                <div className="container-site">
                    <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 items-stretch">
                        <Reveal className="h-full">
                            <div className="h-full min-h-[300px] rounded-2xl overflow-hidden shadow-[0_30px_70px_rgba(6,60,53,0.14)]">
                                <img
                                    src={IMG.hero}
                                    alt="An experienced Indian doctor consulting an international patient in a modern, calm clinic"
                                    data-testid="medical-care-image"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        </Reveal>
                        <Reveal delay={0.1} className="h-full">
                            <div className="h-full bg-forest text-ivory rounded-2xl p-8 sm:p-10 flex flex-col justify-center">
                                <LeafMark className="w-7 h-7 text-natural" />
                                <h2 className="mt-5 font-serif font-medium text-2xl sm:text-3xl leading-snug">
                                    What we coordinate,{" "}
                                    <span className="italic text-natural">end to end.</span>
                                </h2>
                                <ul className="mt-6 space-y-4">
                                    {COORDINATED_ITEMS.map((item) => (
                                        <li key={item.title} className="flex items-start gap-3">
                                            <LeafMark className="w-3.5 h-3.5 mt-1 text-natural shrink-0" />
                                            <span>
                                                <span className="block text-sm font-semibold">{item.title}</span>
                                                <span className="mt-0.5 block text-xs leading-relaxed text-ivory/65">{item.desc}</span>
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section className="section-pad bg-white" data-testid="medical-care-specialties">
                <div className="container-site">
                    <SectionHeading
                        index="—"
                        eyebrow={t("medical_care.eyebrow", "Specialties")}
                        title={
                            <>
                                Current priority{" "}
                                <span className="italic text-pine">specialties.</span>
                            </>
                        }
                        intro={t("medical_care.intro", "Our initial focus areas at launch. Specialist availability is confirmed case by case, based on your needs.")}
                    />
                    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {SPECIALTIES.map((spec, i) => {
                            const Icon = ICONS[spec.icon] || LeafMark;
                            return (
                                <Reveal key={spec.slug} delay={i * 0.07} className="h-full">
                                    <Link
                                        to={`/specialties/${spec.slug}`}
                                        data-testid={`medical-care-specialty-${spec.slug}`}
                                        className="group h-full flex flex-col bg-ivory border border-pine/10 rounded-2xl overflow-hidden card-lift hover:border-pine/30"
                                    >
                                        <span className="relative block h-40 overflow-hidden">
                                            <img
                                                src={IMG[spec.img]}
                                                alt={spec.imgAlt}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                                loading="lazy"
                                            />
                                        </span>
                                        <span className="flex flex-col flex-1 p-6">
                                            <span className="flex items-center gap-3">
                                                <Icon className="w-5 h-5 text-forest shrink-0" strokeWidth={1.5} />
                                                <span className="font-serif font-medium text-xl text-forest">{spec.name}</span>
                                            </span>
                                            <span className="block mt-2 text-sm leading-relaxed text-moss flex-1">{spec.desc}</span>
                                            <span className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-forest">
                                                {t("medical_care.text1", "View specialty")}
                                                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                                            </span>
                                        </span>
                                    </Link>
                                </Reveal>
                            );
                        })}
                    </div>
                    <Reveal delay={0.15}>
                        <p className="mt-8 text-xs text-moss max-w-2xl">
                            {t("medical_care.text2", "Other areas of care are available — share your case and we will confirm appropriate specialist availability.")}
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="section-pad bg-ivory" data-testid="medical-care-process">
                <div className="container-site">
                    <SectionHeading
                        index="—"
                        eyebrow={t("medical_care.eyebrow", "How It Works")}
                        title={t("medical_care.title1", "A clear process, from first message to follow-up.")}
                    />
                    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
                        {HOW_IT_WORKS.map((step, i) => {
                            const Icon = ICONS[step.icon] || LeafMark;
                            return (
                                <Reveal key={step.number} delay={i * 0.08} className="h-full">
                                    <div data-testid={`medical-care-step-${i + 1}`} className="h-full border-t-2 border-pine/20 pt-6">
                                        <span className="flex items-center gap-3">
                                            <Icon className="w-5 h-5 text-forest" strokeWidth={1.5} />
                                            <span className="font-serif italic text-2xl text-natural">{step.number}</span>
                                        </span>
                                        <h3 className="mt-3 font-serif font-medium text-xl text-forest">{step.title}</h3>
                                        <p className="mt-2 text-sm leading-relaxed text-moss">{step.desc}</p>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                    <Reveal delay={0.1}>
                        <p className="mt-8 text-xs leading-relaxed text-moss max-w-2xl">
                            {t("medical_care.text3", "UniversHeal coordinates healthcare, wellness and Ayurvedic experiences through external providers. Medical diagnosis and treatment decisions remain the responsibility of qualified healthcare professionals and providers.")}
                        </p>
                    </Reveal>
                </div>
            </section>

            <CtaBand
                headline={t("medical_care.headline", "Begin with a medical opinion.")}
                text={t("medical_care.text", "Share your case — reports can be shared securely after your enquiry, and specialist review is often the first step.")}
            />
        </>
    );
};
